/**
 * @fileoverview Success page component for completed donations.
 * Displays thank you message and provides option to donate again or return to stream.
 */

import parse from 'html-react-parser';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { bindIsDonationDone, bindPaymentOrderNo, bindShowHideForm } from '../../../actions';
import { PAGES } from '../utility/pages';
import { formatSuccessMessage } from '../utility/util';


/**
 * Success page component displayed after a successful donation.
 * Shows customizable thank you message and provides navigation options.
 *
 * @param {Object} props - Component props
 * @param {Object} props.udata - User data from the donation
 * @param {number} props.totalAmount - Total donation amount
 * @param {boolean} props.donateAgain - Whether to show donate again option
 * @param {Function} props.onNavigate - Navigation function
 * @returns {ReactElement} The success page component
 */
const Success = props => {
    const { t } = useTranslation();

    const formSettings = useSelector(state => state['features/clickandpledge']?.formSettings || {});
    const paymentOrderNo = useSelector(state => state['features/clickandpledge']?.paymentOrderNo || '');
    const streamoptions = useSelector(state => state['features/clickandpledge']?.streamOptions || {});

    const dispatch = useDispatch();

    // Get udata and totalAmount from props instead of location state
    const { udata, totalAmount } = props;

    // SVG path data for the success icon
    const successIconPath = 'M4.7 6.5L3.6 7.6l3.6 3.6 8-8 -1.1-1.1L7.2 9 4.7 6.5z'
        + 'M14.4 8c0 3.5-2.9 6.4-6.4 6.4 -3.5 0-6.4-2.9-6.4-6.4 0-3.5 2.9-6.4 6.4-6.4 0.6 0 1.2 0.1 1.8 0.2L11 0.6C10.1 0.2 9 0 8 0 3.6 0 0 3.6 0 8c0 4.4 3.6 8 8 8 4.4 0 8-3.6 8-8H14.4zM14.4 8';


    useEffect(() => {
        window.scrollTo(0, 0);

    }, [ formSettings.ThankyouMessage ]);


    /**
     * Handles the "donate again" functionality.
     * Resets payment state and navigates back to the donation form.
     * Preserves donation amount and user contact info for convenience.
     */
    const donateAgain = () => {
        dispatch(bindPaymentOrderNo(''));
        dispatch(bindIsDonationDone(false));

        // If this is "View Stream" (SPS === 1), we want to show the streamer
        if (streamoptions.SPS === 1) {
            // Call showStream to hide the form and show streamer
            dispatch(bindShowHideForm(false)); // Hide the donation form
            props.onNavigate(PAGES.LOAD_FORM, { showDForm: false,
                hideHeader: false });

        } else {
            // For regular "donate again", show the form
            // Note: We don't reset donation amount or contact info to maintain persistence
            dispatch(bindShowHideForm(true));
            props.onNavigate(PAGES.LOAD_FORM, { showDForm: true,
                hideHeader: false });

        }
    };

    return (
        <div className = 'thankyou-container'>

            <svg
                className = 'success-icon'
                height = '30'
                viewBox = '0 0 16 16'
                width = '30'
                xmlns = 'https://www.w3.org/2000/svg'>
                <path
                    d = { successIconPath }
                    fill = { formSettings.ThankYouMessageSettings && formSettings.ThankYouMessageSettings.iconColor
                        ? formSettings.ThankYouMessageSettings.iconColor
                        : '#6DA77A' } />
            </svg>

            {formSettings.ThankyouMessage ? (
                <div
                    className = 'thankyou-message-content'

                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML = {{ __html: parse(formatSuccessMessage(
                        formSettings.ThankyouMessage,
                        udata,
                        totalAmount,
                        paymentOrderNo,
                        formSettings.Currency
                    )) }}
                    id = 'thankyou-message' />
            ) : null}

            {props.donateAgain ? (
                <div className = 'form_input_wrapper d-flex justify-content-between mt_2'>
                    <button
                        className = 'full_button tabindex_btn next success-back-button'
                        data-index = '0'
                        onClick = { donateAgain }
                        type = 'button'>
                        {' '}
            &#60; {streamoptions.SPS === 1 ? t('clickandpledge.ViewStream') : t('clickandpledge.Back')} {' '}
                    </button>
                </div>
            ) : null}


        </div>
    );
};

export default Success;
